import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';
import { getAnalysisLatest, runAnalysis, AnalysisResult } from '@/api/analysis';

/**
 * Mailbox Insight Card
 * Shows latest mailbox analysis results
 */
export const MailboxInsightCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [data, setData] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(false);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const result = await getAnalysisLatest();
        setData(result);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [onError]);

  const handleAction = async (action: string) => {
    try {
      setActionInProgress(true);
      if (action === 'Analyse inbox') {
        await runAnalysis();
        const updated = await getAnalysisLatest();
        setData(updated);
      }
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '1rem', color: '#666' }}>Loading mailbox insights...</div>;
  }

  if (!data) {
    return <div style={{ padding: '1rem', color: '#666' }}>No mailbox data available</div>;
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <strong>Status:</strong> {data.status}
        </div>
        {data.metricsComputedAt && (
          <div style={{ marginBottom: '0.75rem' }}>
            <strong>Last Analysis:</strong>{' '}
            {new Date(data.metricsComputedAt).toLocaleDateString()}
          </div>
        )}
        {data.messageCount !== undefined && (
          <div style={{ marginBottom: '0.75rem' }}>
            <strong>Messages Analysed:</strong> {data.messageCount}
          </div>
        )}
        {data.daysAnalysed !== undefined && (
          <div style={{ marginBottom: '1rem' }}>
            <strong>Days of Data:</strong> {data.daysAnalysed}
          </div>
        )}
        {data.status === 'none' && (
          <div style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
            No analysis has been run yet.
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {card.cardActionsList.map((action: string) => (
          <button
            key={action}
            onClick={() => handleAction(action)}
            disabled={actionInProgress}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionInProgress ? 'not-allowed' : 'pointer',
              opacity: actionInProgress ? 0.6 : 1,
            }}
          >
            {action}
          </button>
        ))}
      </div>
    </div>
  );
};

MailboxInsightCard.displayName = 'MailboxInsightCard';

export default MailboxInsightCard;
